import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { api, extractApiError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { formatDate } from '@/lib/utils';

export function ComplianceScanDetailPage() {
  const { id } = useParams();
  const qc = useQueryClient();

  const scan = useQuery({
    queryKey: ['compliance-scan', id],
    queryFn: async () => (await api.get(`/api/compliance/scans/${id}`)).data,
  });

  const updateFinding = useMutation({
    mutationFn: async ({ findingId, status }: { findingId: string; status: string }) =>
      api.patch(`/api/compliance/findings/${findingId}`, { status }),
    onSuccess: () => {
      toast.success('Finding updated');
      qc.invalidateQueries({ queryKey: ['compliance-scan', id] });
    },
    onError: (err) => toast.error('Update failed', extractApiError(err).message),
  });

  if (scan.isLoading) return <div>Loading...</div>;
  if (!scan.data) return <div>Not found</div>;

  const s = scan.data;
  const findings: Finding[] = s.findings ?? [];

  const bySeverity = {
    critical: findings.filter((f) => f.severity === 'critical'),
    major: findings.filter((f) => f.severity === 'major'),
    minor: findings.filter((f) => f.severity === 'minor'),
    observation: findings.filter((f) => f.severity === 'observation'),
  };

  return (
    <div className="space-y-6">
      <div>
        <Button asChild variant="ghost" size="sm" className="-ml-2 mb-2">
          <Link to="/compliance"><ArrowLeft className="mr-2 h-4 w-4" /> Compliance</Link>
        </Button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">{s.scanType.replace(/_/g, ' ')}</h1>
            <p className="text-sm text-muted-foreground">{s.targetRef} · {formatDate(s.startedAt)}</p>
          </div>
          {s.readinessScore != null && (
            <div className="text-right">
              <div className="text-3xl font-bold">{s.readinessScore.toFixed(0)}%</div>
              <div className="text-xs text-muted-foreground">Readiness</div>
            </div>
          )}
        </div>
      </div>

      {s.summary && (
        <Card>
          <CardHeader><CardTitle>Summary</CardTitle></CardHeader>
          <CardContent><p className="text-sm">{s.summary}</p></CardContent>
        </Card>
      )}

      <div className="grid gap-4 md:grid-cols-4">
        <StatCard label="Total checks" value={s.totalChecks} />
        <StatCard label="Passed" value={s.passedChecks} variant="success" />
        <StatCard label="Failed" value={s.failedChecks} variant="destructive" />
        <StatCard label="Warnings" value={s.warningChecks} variant="warning" />
      </div>

      {(['critical', 'major', 'minor', 'observation'] as const).map((sev) =>
        bySeverity[sev].length > 0 ? (
          <Card key={sev}>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2">
                <SeverityBadge s={sev} /> {bySeverity[sev].length} {sev} finding{bySeverity[sev].length === 1 ? '' : 's'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {bySeverity[sev].map((f) => (
                <div key={f.id} className="rounded-md border p-3">
                  <div className="mb-1 flex items-center justify-between">
                    <div>
                      <span className="text-xs font-mono text-muted-foreground">{f.ruleReference}</span>
                      <span className="ml-2 text-xs text-muted-foreground">{f.category}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusBadge s={f.status} />
                      {f.status !== 'closed' && (
                        <select
                          value={f.status}
                          onChange={(e) => updateFinding.mutate({ findingId: f.id, status: e.target.value })}
                          className="h-7 rounded border border-input bg-background px-2 text-xs"
                        >
                          <option value="open">Open</option>
                          <option value="acknowledged">Acknowledged</option>
                          <option value="remediated">Remediated</option>
                          <option value="closed">Closed</option>
                        </select>
                      )}
                    </div>
                  </div>
                  <div className="text-sm font-medium">{f.finding}</div>
                  {f.evidence && (
                    <div className="mt-1 rounded bg-muted p-2 text-xs">
                      <span className="text-muted-foreground">Evidence:</span> {f.evidence}
                    </div>
                  )}
                  {f.recommendation && (
                    <div className="mt-1 text-xs">
                      <span className="font-medium">Recommendation:</span> {f.recommendation}
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        ) : null,
      )}
    </div>
  );
}

interface Finding {
  id: string;
  severity: string;
  category: string;
  ruleReference: string;
  finding: string;
  recommendation: string | null;
  evidence: string | null;
  status: string;
}

function SeverityBadge({ s }: { s: string }) {
  const v: Record<string, 'destructive' | 'warning' | 'secondary' | 'info'> = {
    critical: 'destructive',
    major: 'warning',
    minor: 'secondary',
    observation: 'info',
  };
  return <Badge variant={v[s] ?? 'secondary'}>{s.toUpperCase()}</Badge>;
}

function StatusBadge({ s }: { s: string }) {
  const v: Record<string, 'warning' | 'info' | 'success' | 'secondary'> = {
    open: 'warning',
    acknowledged: 'info',
    remediated: 'success',
    closed: 'secondary',
  };
  return <Badge variant={v[s] ?? 'secondary'}>{s}</Badge>;
}

function StatCard({ label, value, variant }: { label: string; value: number | string; variant?: 'success' | 'destructive' | 'warning' }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className={`text-2xl font-bold ${variant === 'success' ? 'text-emerald-600' : variant === 'destructive' ? 'text-destructive' : variant === 'warning' ? 'text-amber-600' : ''}`}>
          {value}
        </div>
        <div className="text-xs text-muted-foreground">{label}</div>
      </CardContent>
    </Card>
  );
}
